/**
 * Player and other controls
 *
 * @copyright 2018 Linguistica 360
 */

import 'jquery-ui/ui/widgets/draggable.js';
import 'jquery-ui/ui/widgets/slider.js';
import 'stickUp/build/js/stickUp.min.js';

import '../libs/jquery.ui.touch-punch.js';
import { $wrap, resolveRoot } from '../shared/utils.js';
import { Script, injector } from '../shared/injector.js';

import $ from 'jquery';
import MicroModal from 'micromodal';
import { EventEmitter } from '../shared/event-emitter.js';
import capitalize from 'lodash/capitalize.js';
import clamp from 'lodash/clamp.js';
import debounce from 'lodash/debounce.js';
import iziToast from 'izitoast';

import { PROGRAM } from '../shared/config.js';


// all events we want to subscribe to not including timeupdate
const EVENTS = ['pause', 'ended', 'playing', 'canplay', 'loadstart'];

const SEEK_STEP = 5;
const CUTOFF_TIME = 60; // seconds
const CUTOFF_TIMEOUT = 15000; // milliseconds

export let gAudio = null;

function _timeFormat(fSeconds) {
    let minutes = Math.floor(fSeconds / 60);
    let seconds = Math.floor(fSeconds - minutes * 60);

    if (minutes < 10) minutes = `0${minutes}`;
    if (seconds < 10) seconds = `0${seconds}`;

    return `${minutes}:${seconds}`;
}


class Player extends Script {
    isInit = false;

    constructor($player) {
        super();

        this.loading = false;
        this.delayedInit = false;

        this.chromeFix = false;

        this.ee = new EventEmitter();

        this.initEvents();

        this.$player = $player;
        this.$buttons = this.$player.find('.player-control');
        this.$toggle = this.$player.find('.player-toggle');

        this.$toggle.on('click', $wrap(($button) => {
            if ($button.data('disabled')) return;

            if ($button.is('.player-play')) return this.actionPlay();
            else return this.actionPause();
        })).addClass('init');

        this.cutoff = this.$player.data('cutoff') == true;

        this.showCutoff = debounce(() => this._showCutoff(), 250, {
            leading: true, trailing: false,
        });

        this.$timer = this.$player.find('.timer');
    }

    once(event, callback) {
        this.ee.once(event, callback);
    }

    trigger(event, ...args) {
        this.ee.emit(event, ...args);
    }

    actionPlay() {
        this.isInit = true;
        const initTime = this.$player.data('init-time');
        if (initTime) {
            this.$player.removeData('init-time');
            this.seek(initTime, true);
            return;
        }

        if (this.loading) return;
        if (gAudio.paused === false) this._pausePlay();
        else this._play();

        return false; // do not bubble, fully handled
    }

    actionPause() {
        if (this.loading) return;
        if (gAudio.paused === false) this._pause();
    }

    rewind() {
        if (this.$toggle.eq(0).data('disabled')) return;
        this.seek(-SEEK_STEP);
    }

    forward() {
        if (this.$toggle.eq(0).data('disabled')) return;
        this.seek(SEEK_STEP);
    }

    seek(value, absolute = false) {
        if (!this.isMe() || gAudio.paused) {
            if (!this.isInit && absolute) {
                this.$player.data('init-time', value);
            } else {
                this.once('playing', () => this._seek(value, absolute));
            }

            this.actionPlay();
        }
        else this._seek(value, absolute);
    }

    _seek(value, absolute = false) {
        // 0.1 to avoid reloading of the audio (not sure why it happens)
        const high = gAudio.duration - 0.1;
        const svalue = absolute ? 0.0 : (gAudio.currentTime || 0.0);
        const cvalue = clamp(svalue + value, 0.1, high);

        gAudio.currentTime = cvalue;
    }

    initEvents() {
        this.ee.on('stop', () => this.eventStop());

        this.ee.on('timeupdate', (time, duration) => {
            if (this.eventTime) this.eventTime(time, duration);
        });

        EVENTS.forEach((event) => {
            const methodName = `event${capitalize(event)}`;
            if (this[methodName]) {
                this.ee.on(event, () => this[methodName]());
            }

            if (['pause', 'ended'].includes(event) && this.eventStop) {
                this.ee.on(event, () => this.ee.emit('stop'));
            }
        });
    }

    setSrc(src) {
        if (gAudio.paused === false) {
            const instance = $(gAudio).data('instance');
            instance.once('stop', () => this._setSrc(src));
            this._pause();
        }
        else this._setSrc(src);
    }

    _setSrc(src) {
        if (this.$timer.length) this.$timer.css('visibility', 'hidden');
        if (this.$speed.length) {
            this.$speed.css('visibility', 'hidden');
            this.$speed.find('.speed').html('1.0');
        }

        this.chromeFix = false;
        this.delayedInit = true;
        gAudio.src = src;
    }

    _deactivate() {
        if (this.$timer.length) this.$timer.css('visibility', 'hidden');
        if (this.$speed.length) {
            this.$speed.css('visibility', 'hidden');
            this.$speed.find('.speed').html('1.0');
        }
    }

    _play() {
        this.$buttons.data('disabled', true);

        if (!this.isMe()) {
            const instance = $(gAudio).data('instance');
            if (instance) instance._deactivate();

            this._setSrc(this.$player.data('src'));
            $(gAudio).data('instance', this);
        }

        if (this.delayedInit) {
            this.delayedInit = false;
            this.eventLoadstart();
        }

        gAudio.play();
    }

    _pause() {
        gAudio.pause();
        this.loading = false;
    }

    _pausePlay() {
        const instance = $(gAudio).data('instance');
        instance.once('stop', () => this.actionPlay());
        instance.actionPause();
    }

    eventPlaying() {
        this.$buttons.show();
        this.$buttons.removeData('disabled');
        this.$player.removeData('init-time');
        this.$toggle.addClass('player-pause').removeClass('player-play');
        this.$speed.css('visibility', 'visible');
    }

    eventCanplay() {
        this.loading = false;
        this.delayedInit = false;

        // only chrome is affected by this bug, but it looks like that
        // firefox and safari can handle the fix well too, so let's only
        // disable the fix for edge which can't handle it well
        if (!navigator.userAgent.includes('Edge')) this.chromeFix = true;
    }

    eventStop() {
        this.$toggle.addClass('player-play').removeClass('player-pause');
    }

    eventTime(time, duration) {
        if (this.chromeFix && time == 0) {
            this.chromeFix = false;
            this._setSrc(gAudio.src);
            this._play();

            return false;
        }

        this.$player.trigger('event-player-time', [time, duration]);

        if (this.cutoff && time > CUTOFF_TIME) {
            this._pause();
            this.showCutoff();
        }

        if (duration && this.$timer.length) {
            if (this.$timer.css('visibility') !== 'visible') {
                this.$timer.css('visibility', 'visible');
            }

            this.$timer.find('.timer-time').html(_timeFormat(time));
            this.$timer.find('.timer-duration').html(_timeFormat(duration));
        }

        return true;
    }

    eventLoadstart() {
        if (!this.delayedInit) this.loading = true;
    }

    isMe() {
        return $(gAudio).data('instance') === this;
    }

    _showCutoff() {
        if (this.toast) {
            const transition = { transitionOut: 'fadeOut' };
            iziToast.hide(transition, this.toast);
        }

        let title = 'To access this material, please Sign In';
        let message = "If you don't have a subscription, please Subscribe for this program";

        let buttons = [
            ['<button><b>Sign In</b></button>', () => {
                MicroModal.show('modal_login');
            }, true],
            ['<button><b>Subscribe</b></button>', () => {
                location.href = resolveRoot('subscription-rates');
            }, true],
        ];

        const edition = this.$player.data('other-edition');
        if (edition) {
            title = `You are subscribed to the ${edition} edition`;
            message = 'Switch between editions on the main menu';
            buttons = [];
        }

        iziToast.error({
            title,
            message,
            timeout: CUTOFF_TIMEOUT,
            buttons,
            onOpening: (_, toast) => this.toast = toast,
            onClosing: () => this.toast = null,
        });
    }
}

class Inline extends Player {
    constructor($player) {
        super($player);
    }

    actionPlay() {
        super.actionPlay();
        return false; // do not bubble to prevent reinitialisation
    }

    eventPlaying() {
        super.eventPlaying();
        this.$player.find('.player-playing').addClass('playing');
    }

    eventStop() {
        super.eventStop();
        this.$player.find('.player-playing').removeClass('playing');
    }

    _play() {
        this.$buttons.hide();
        super._play();
    }
}

class Controls extends Player {
    constructor($controls) {
        super($controls.find('.player'));
        this.$timer = $controls.find('.timer');
        this.$speed = $controls.find('.player-speed');

        this.$controls = $controls;
        this.$volume = this.$controls.find('.player-volume');
        this.$volumeWrapper = this.$controls.find('.player-volume-wrapper');

        // TODO
        if ($controls.find('.playlist-controls-autoplay').length) {
            this.autoplay = localStorage.getItem('playback-autoplay') === 'true';
        }

        this.initControls();

        $controls.find('.download-pdf.cff').on('click', () => {
            this.showCutoff();
        });
        $controls.find('.download-audio.cff').on('click', () => {
            this.showCutoff();
        });

        this.initAddToList();
        this.initPlaylistControls();

        if (this.autoplay) this.actionPlay();

        const $as = this.$controls.parent().find('.audio-anchor');
        $as.on('click', $wrap(($a) => {
            const time = $a.data('time');
            this.seek(time, true);
        }));
        $as.on('my-tab-clicked', $wrap(($a) => {
            if (this.cutoff) return;
            const time = $a.data('time');
            this.seek(time, true);
        }));
    }

    initControls() {
        this.initButtons();
        this.initHandle();
        this.initSlow();
        this.initVolume();
        this.initPlaylist();
    }

    initVolume() {
        if (!this.$volume.length || !this.$volumeWrapper.length) return;

        this.volumeMute = 1;
        this.volumeTimeout = null;

        this.$volume.on('mouseover', () => this.openVolume());
        this.$volume.on('mouseleave', () => this.hideVolume());

        this.$volume.on('click', () => {
            if (!this.$volume.is('.mute')) {
                this.volumeMute = gAudio.volume;
                this.setVolume(0, true);
            }
            else this.setVolume(this.volumeMute, true);
        });

        this.$volumeWrapper.on('mouseover', () => this.openVolume());
        this.$volumeWrapper.on('mousleave', () => this.hideVolume());

        this.$volumeWrapper.find('.volume-slider').slider({
            range: 'min',
            min: 0,
            max: 100,
            value: Math.floor(gAudio.volume * 100),
            change: (_, ui) => this.setVolume(ui.value / 100),
        });

        this.$volumeOverlay = $('<div class="volume-wrapper-overlay">');
        $('body').append(this.$volumeOverlay);

        this.$volumeOverlay.on('mouseover', () => this.hideVolume());
        this.$volumeOverlay.on('click', () => this.hideVolume());
    }

    initPlaylist() {
        const playlist = this.$controls.data('playlist');
        if (!playlist) return;

        const $playlist = $(playlist);
        if (!$playlist.length) return;

        $playlist.find('li:first-child').addClass('active');

        const self = this;
        $playlist.on('click', 'li:not(.active)', function () {
            const $this = $(this);
            self.setSrc($this.data('src'));
            self.$player.data('src', $this.data('src'));
            $playlist.find('li.active').removeClass('active');
            $this.addClass('active');
        });
    }

    openVolume() {
        clearTimeout(this.volumeTimeout);
        this.volumeTimeout = null;

        this.$volumeWrapper.addClass('opened');
        this.$volumeOverlay.show();
    }

    hideVolume() {
        if (this.volumeTimeout) return;

        this.volumeTimeout = setTimeout(() => {
            this.$volumeWrapper.removeClass('opened');
            this.$volumeOverlay.hide();
        }, 750);
    }

    setVolume(value, updateUi = false) {
        gAudio.volume = value;

        if (value) this.$volume.removeClass('mute');
        else this.$volume.addClass('mute');

        if (updateUi) {
            const val = Math.floor(value * 100);
            this.$volumeWrapper.find('.volume-slider').slider('value', val);
        }
    }

    initButtons() {
        // forward / rewind
        this.$player.find('.player-rewind').on('click', this.wrap(this.rewind));
        this.$player.find('.player-forward').on('click', this.wrap(this.forward));
    }

    initHandle() {
        this.$handle = this.$controls.find('.draggable');
        this.$bar = this.$controls.find('.bar');
        this.$barParent = this.$handle.parent();

        let resume = false;
        this.$handle.draggable({
            drag: () => {
                const othis = this.$handle.offset().left;
                const hwidth = this.$handle.width() / 2;
                const convert = othis - this.$barParent.offset().left + hwidth;
                const width = convert / this.$barParent.width();

                this.$bar.width(`${width * 100}%`);
            },

            start: () => {
                resume = !gAudio.paused;
                if (resume) gAudio.pause();
            },

            stop: () => {
                const width = this.$bar.width();
                const total = this.$barParent.width();

                const seek = gAudio.duration * (width / total);
                gAudio.currentTime = Math.floor(seek);
                if (resume) this._play();
            },

            axis: 'x',
            containment: this.$controls.find('.audio-progress'),
        });

        const $series = this.$controls.parents('.series-catalog-sticky');
        if ($series.length) {
            $series.stickUp({ keepInWrapper: true });
        } else if (!this.$controls.hasClass('nostick')) {
            this.$controls.stickUp({ keepInWrapper: true });
        }
    }

    initSlow() {
        const src = this.$player.data('src');

        this.$controls.on('click', '.player-speed-normal', $wrap(($button) => {
            if (this.loading) return;

            $button.addClass('player-speed-slow')
                .removeClass('player-speed-normal');

            this.$player.data('src', src);
            $('.download-audio').attr('href', src);
            this.setSrc(src);
        }));

        this.$controls.on('click', '.player-speed-slow', $wrap(($button) => {
            if (this.loading) return;

            $button.addClass('player-speed-normal')
                .removeClass('player-speed-slow');

            const newsrc = src.replace('.mp3', 'n.mp3');
            this.$player.data('src', newsrc);
            $('.download-audio').attr('href', newsrc);
            this.setSrc(src.replace('.mp3', 'n.mp3'));

            this.showNormalPopup();
        }));

        let speeds = { '0.5': 0.85, '1.0': 1.0, '1.5': 1.3 };

        if (PROGRAM === 'nsf') {
            speeds = { '0.5': 0.85, '1.0': 1.0, '1.5': 1.15 };
        }

        const length = Object.keys(speeds).length;
        this.$speed.on('click', $wrap(($button) => {
            const speed = $button.find('.speed').html();
            const index = Object.keys(speeds).indexOf(speed);
            const newSpeed = Object.keys(speeds)[(index + 1) % length];
            gAudio.playbackRate = speeds[newSpeed];
            $button.find('.speed').html(newSpeed);
        }));
    }

    eventLoadstart() {
        if (!this.delayedInit) {
            this.$bar.addClass('animated');
            this.$bar.width(this.$barParent.width());
            this.$handle.css('visibility', 'hidden');
        }

        super.eventLoadstart();
    }

    fixControls() {
        if (!this.$barParent.length || gAudio.currentTime <= 0) return;

        const width = this.$bar.css('width');
        const offset = this.$barParent.offset().left + parseFloat(width);

        this.$handle.offset({
            top: this.$barParent.offset().top - this.$handle.height() / 2,
            left: offset - this.$handle.width(),
        });
    }

    _play() {
        super._play();
        this.$toggle.addClass('player-pause').removeClass('player-play');
    }

    _setSrc(src) {
        this.$handle.css('visibility', 'hidden');
        this.$bar.width(0);
        super._setSrc(src);
    }

    eventCanplay() {
        super.eventCanplay();

        this.$bar.removeClass('animated');
        if (gAudio.currentTime === 0) this.$bar.width(0);
    }

    eventTime(time, duration, noWidth) {
        if (!super.eventTime(time, duration)) return false;
        if (time === 0) return false;

        const width = time / duration;
        if (!noWidth) this.$bar.css('width', (width * 100) + '%');

        const loading = this.$bar.hasClass('animated');
        if (!loading && gAudio.currentTime < CUTOFF_TIMEOUT) {
            this.$handle.css('visibility', 'visible');
        }

        this.fixControls();

        return true;
    }

    eventEnded() {
        if (this.autoplay) {
            const $next = $('.playlist-controls-item.right');
            if ($next.data('url')) {
                let cancelled = false;
                iziToast.warning({
                    title: 'Navigating to the next track',
                    message: $next.data('tooltip'),
                    buttons: [
                        ['<button><b>CANCEL</b></button>', () => {
                            cancelled = true;
                        }, true],
                    ],
                    onClosed: () => {
                        if (!cancelled) {
                            location.href = resolveRoot($next.data('url'));
                        }
                    },
                });
            }
        }
    }

    _showCutoff() {
        super._showCutoff();
        this.$handle.css('visibility', 'hidden');
    }

    showNormalPopup() {
        if (localStorage.getItem('normal-popup-shown') === 'true') return;

        const transition = { transitionOut: 'fadeOut' };

        iziToast.info({
            timeout: 10000,
            close: true,
            overlay: true,
            toastOnce: true,
            zindex: 999,
            title: 'Normal speed',
            message: 'The normal speed version of the news story contains ONLY news narration, not dialog.',
            position: 'center',
            buttons: [
                ['<button class="ok">OK</button>', (instance, toast) => {
                    instance.hide(transition, toast, 'button');
                }],
                ['<button class="never">Don\'t show it again!</button>', (instance, toast) => {
                    localStorage.setItem('normal-popup-shown', 'true');
                    instance.hide(transition, toast, 'button');
                }],
            ],
        });
    }

    initAddToList() {
        const $button = this.$controls.find('[data-action=add-to-list]');
        const $checkbox = $button.find('input[type=checkbox]');

        const $popup = $button.find('ul.add-to-list-popup').empty();
        $popup.append('<div class="loading">');

        $checkbox.on('change', () => {
            if ($checkbox.is(':checked')) $button.addClass('active');
            else $button.removeClass('active');

            this._ajaxListPopup($button);
        });
    }

    initPlaylistControls() {
        const $controls = this.$controls.find('.playlist-controls');

        let timeout = null;

        $controls.on('mouseover', () => {
            clearTimeout(timeout);
            $controls.addClass('expanded');
        });

        $controls.on('mouseleave', () => {
            timeout = setTimeout(() => {
                $controls.removeClass('expanded');
            }, 1000);
        });

        $controls.on('click', '.playlist-controls-item[data-url]', $wrap(($a) => {
            location.href = resolveRoot($a.data('url'));
        }));

        const $autoplay = $controls.find('.playlist-controls-autoplay');

        const renderAutoplay = () => {
            if (this.autoplay) $autoplay.addClass('enabled');
            else $autoplay.removeClass('enabled');
            $autoplay.show();
        };
        renderAutoplay();

        $autoplay.on('click', () => {
            this.autoplay = !this.autoplay;
            localStorage.setItem('playback-autoplay', this.autoplay ? 'true' : false);
            renderAutoplay();
        });
    }

    _ajaxListPopup($button) {
        const type = $button.data('type');
        const id = $button.data('id');

        const url = resolveRoot(`ajax/playlist-query-item/${type}/${id}`);
        $.getJSON(url).then((res) => {
            this._ajaxListBuild($button, res.data);
        }).catch((res) => {
            console.error(res);
        });
    }

    _ajaxListBuild($button, records) {
        const $popup = $button.find('ul.add-to-list-popup').empty();
        $popup.empty();

        $popup.append($('<li class="title">add to playlist</li>'));

        const $a = $('<a>').append('Create new');
        $a.on('click', () => {
            const name = prompt('Playlist name');
            if (!name) return;

            $.ajax({
                url: resolveRoot('ajax/playlist'),
                method: 'POST',
                dataType: 'json',
                data: { name },
            }).then(() => this._ajaxListPopup($button));
        });
        $popup.append($('<li class="create">').append($a));

        records.forEach((record) => {
            const $a = $('<a>').append(record.name);
            if (record.hasItem) {
                $a.append('<span style="color: #c3272f"> (remove)</span>');
                $a.on('click', () => this._ajaxListDelete($button, record));
            } else {
                $a.on('click', () => this._ajaxListAdd($button, record));
            }
            $popup.append($('<li>').append($a));
        });
    }

    _ajaxListAdd($button, playlist) {
        const type = $button.data('type');
        const record = $button.data('id');

        $.ajax({
            url: resolveRoot(`ajax/playlist/${playlist.id}/add`),
            method: 'POST',
            data: { type, record },
        }).then(() => {
            this._ajaxListPopup($button);
            iziToast.success({ title: `Added to ${playlist.name}` });
        }).catch(() => {
            iziToast.fail({ title: `Failed adding to ${playlist.name}` });
        });
    }

    _ajaxListDelete($button, playlist) {
        const type = $button.data('type');
        const record = $button.data('id');

        $.ajax({
            url: resolveRoot(`ajax/playlist/${playlist.id}/${type}/${record}`),
            method: 'DELETE',
        }).then(() => {
            this._ajaxListPopup($button);
            iziToast.success({ title: `Removed from ${playlist.name}` });
        }).catch(() => {
            iziToast.fail({ title: `Failed removing from ${playlist.name}` });
        });
    }
}


injector.injectClass(class extends Script {
    onLoad() {
        this.controls = [];

        this.initPlayer();
        this.initInlines();
        this.initControls();
        this.initKeyboard();

        $('body').on('click', '[data-flashcard-nop]', () => {
            if (this.toast) {
                const transition = { transitionOut: 'fadeOut' };
                iziToast.hide(transition, this.toast);
            }

            iziToast.error({
                title: 'To access this material, please Sign In',
                message: "If you don't have a subscription, please Subscribe for this program",
                timeout: CUTOFF_TIMEOUT,
                buttons: [
                    ['<button><b>Sign In</b></button>', () => {
                        MicroModal.show('modal_login');
                    }, true],
                    ['<button><b>Subscribe</b></button>', () => {
                        location.href = resolveRoot('subscription-rates');
                    }, true],
                ],
                onOpening: (_, toast) => this.toast = toast,
                onClosing: () => this.toast = null,
            });
        });
    }

    initPlayer() {
        const $audio = $('<audio>').css('display', 'none').appendTo('body');

        //TODO
        //const autoplay = !!localStorage.getItem('playback-autoplay');
        //$audio[0].autoplay = autoplay;

        gAudio = $audio.get(0);

        EVENTS.forEach((event) => {
            $audio.on(event, () => {
                const instance = $audio.data('instance');
                if (instance) instance.trigger(event);
            });
        });

        $audio.on('timeupdate', () => {
            const instance = $audio.data('instance');
            const time = gAudio.currentTime;
            const duration = gAudio.duration;
            if (instance) instance.trigger('timeupdate', time, duration);
        });
    }

    initInlines() {
        // initialise inline players in place
        $('body').on('click', '.player-play', function () {
            const $player = $(this).closest('.inline-player');

            if ($player.length) {
                const inlinePlayer = new Inline($player);
                inlinePlayer.actionPlay();
            }
        });
    }

    initControls() {
        $('.controls').each($wrap(($this) => {
            this.controls.push(new Controls($this));
        }));

        if (!$('.controls').length) {
            const $series = $('.series-catalog-sticky');
            if ($series.length) {
                $series.stickUp({ keepInWrapper: true });
            }
        }
    }

    initKeyboard() {
        $(document).on('keydown', (e) => {
            if (e.target.matches('input')) {
                return;
            }

            const instance = $(gAudio).data('instance');
            if (!instance) return; // no active player

            switch (e.code) {
                case 'Space': // space
                    if (gAudio.paused) instance.actionPlay();
                    else instance.actionPause();
                    e.preventDefault();
                    break;
                case 'ArrowLeft': // left
                    instance.rewind();
                    e.preventDefault();
                    break;
                case 'ArrowRight': // right
                    instance.forward();
                    e.preventDefault();
                    break;
                case 'Equal': // +
                case 'NumpadAdd':
                    if (instance instanceof Controls) {
                        const volume = Math.floor(gAudio.volume * 10);
                        const newvol = clamp(volume + 1, 0, 10) / 10;
                        instance.setVolume(newvol, true);
                        e.preventDefault();
                    }
                    break;
                case 'Minus': // -
                case 'NumpadSubtract':
                    if (instance instanceof Controls) {
                        const volume = Math.floor(gAudio.volume * 10);
                        const newvol = clamp(volume - 1, 0, 10) / 10;
                        instance.setVolume(newvol, true);
                        e.preventDefault();
                    }
                    e.preventDefault();
                    break;
            }
        });
    }

    onResize() {
        this.controls.forEach((controls) => controls.fixControls());
    }
});
